import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Chip,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Avatar,
    Alert,
    Pagination,
    IconButton,
    TextField,
    useTheme,
    alpha
} from '@mui/material';
import { usePermissions } from '../../../../utils/permissions';
import {
    Add as AddIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Science as ScienceIcon,
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Engineering as EngineeringIcon,
    Description as DescriptionIcon,
    Analytics as AnalyticsIcon,
    VerifiedUser as VerifiedUserIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../../supabaseClient';
import { 
    ConfigurationStep, 
    SpecificationsStep, 
    EnvironmentStep, 
    SamplingStep, 
    ResultsStep, 
    ReviewStep,
    TestCard,
    qcTimelineSteps,
    testTypes
} from './qc-logs-components';

export default function QCChecksLog({ batchId, batchLogId, productInfo, companyId, batchNumber, batchIngredients = [], showSnackbar, onDataUpdate, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const { canAccessPage, canEditPage, canDeletePage, canApproveQCTests } = usePermissions();
    const [qcTests, setQcTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [testUnderReview, setTestUnderReview] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [testToDelete, setTestToDelete] = useState(null);
    const [editingTest, setEditingTest] = useState(null);
    const [expandedTests, setExpandedTests] = useState({});
    const [activeStep, setActiveStep] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const testsPerPage = 3;
    const [stepValidation, setStepValidation] = useState({
        configuration: false,
        specifications: false,
        environment: false,
        sampling: false,
        results: false,
        review: false
    });
    const [formData, setFormData] = useState({
        batch_id: batchId,
        batch_log_id: batchLogId,
        product_id: productInfo?.id || '',
        product_name: productInfo?.name || '',
        product_sku: productInfo?.sku || productInfo?.product_code || '',
        batch_ingredient_id: '',
        test_category: 'in_process',
        test_stage: 'mixing',
        test_type: '',
        test_method: '',
        test_standard: '',
        target_value: '',
        target_unit: '',
        min_acceptable: '',
        max_acceptable: '',
        actual_result: '',
        result_text: '',
        tested_by_name: '',
        tested_by_role: 'qa_technician',
        test_datetime: dayjs(),
        room_temperature_c: '',
        room_humidity_percent: '',
        clean_room_classification: '',
        temp_unit: 'celsius',
        equipment_id: '',
        equipment_name: '',
        equipment_model: '',
        equipment_serial: '',
        calibration_status: 'not_required',
        calibration_date: null,
        calibration_due_date: null,
        sample_id: '',
        sample_location: '',
        sample_quantity: '',
        sample_unit: 'ml',
        sample_retention_location: '',
        qa_review_required: true,
        qa_review_status: 'PENDING',
        notes: '',
        photo_urls: [],
        document_urls: [],
        is_critical_test: false,
        
        // Flexible specification fields
        specification_type: 'numeric',
        acceptance_criteria: '',
        scale_range_min: '',
        scale_range_max: '',
        acceptable_scale_values: '',
        reference_standard: '',
        max_delta_e: '',
        min_time: '',
        max_time: '',
        qa_signature: null,
        qa_signature_name: null,
        qa_signature_role: null,
        qa_signature_date: null,
        qa_review_comments: null,
        qa_reviewed_by_name: null,
        qa_reviewed_by_role: null,
        qa_review_datetime: null,
        executed_by_signature: null,
        executed_by_signature_name: null,
        executed_by_signature_date: null
    });
    const [qcDocumentationConfirmed, setQcDocumentationConfirmed] = useState(false);
    const [qcConfirmationDetails, setQcConfirmationDetails] = useState(null);
    const [confirmationDialog, setConfirmationDialog] = useState(false);
    const [confirmerName, setConfirmerName] = useState('');

    useEffect(() => {
        if (batchId) {
            fetchQCTests();
            loadQCDocumentationStatus();
        } else {
            setLoading(false);
        }
    }, [batchId, companyId]);
        
    useEffect(() => {
        if (!batchId || !companyId) {
            showSnackbar?.('Missing batch or company ID - QC tests may not save properly', 'warning');
        }
    }, [batchId, companyId, showSnackbar]);

    const fetchQCTests = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from('qc_tests')
                .select('*')
                .order('created_at', { ascending: false });
            
            // Only filter by batch_id if it exists, otherwise show all tests for the user
            if (batchId) {
                query = query.eq('batch_id', batchId);
            }
            
            const { data, error } = await query;
            
            if (error) {
                throw error;
            }
            
            setQcTests(data || []);
        } catch (error) {
            showSnackbar?.('Error loading QC tests: ' + error.message, 'error');
            setQcTests([]);
        } finally {
            setLoading(false);
        }
    };

    const loadQCDocumentationStatus = async () => {
        try {
            if (batchLogId) {
                const { data, error } = await supabase
                    .from('batch_logs')
                    .select('qc_documentation_confirmed, qc_documentation_confirmed_by, qc_documentation_confirmed_at')
                    .eq('id', batchLogId)
                    .single();

                if (error) {
                    console.error('Error loading QC documentation status:', error);
                    return;
                }

                setQcDocumentationConfirmed(data?.qc_documentation_confirmed || false);
                if (data?.qc_documentation_confirmed) {
                    setQcConfirmationDetails({
                        confirmedBy: data.qc_documentation_confirmed_by,
                        confirmedAt: data.qc_documentation_confirmed_at
                    });
                }
            }
        } catch (error) {
            console.error('Error loading QC documentation status:', error);
        }
    };

    const handleConfirmQCDocumentation = async () => {
        try {
            if (!batchLogId) {
                showSnackbar?.('No batch log ID available', 'error');
                return;
            }

            if (!confirmerName.trim()) {
                showSnackbar?.('Please enter your name to confirm QC documentation', 'warning');
                return;
            }

            // Check for any pending QC tests that need review
            const pendingTests = qcTests.filter(test => 
                test.qa_review_status !== 'APPROVED' && test.qa_review_status !== 'REVIEWED'
            );

            if (pendingTests.length > 0) {
                showSnackbar?.(`Cannot confirm QC documentation: ${pendingTests.length} test(s) pending review. Please review all tests first.`, 'error');
                return;
            }

            const { error } = await supabase
                .from('batch_logs')
                .update({ 
                    qc_documentation_confirmed: true,
                    qc_documentation_confirmed_at: new Date().toISOString(),
                    qc_documentation_confirmed_by: confirmerName.trim()
                })
                .eq('id', batchLogId);

            if (error) {
                throw error;
            }

            setQcDocumentationConfirmed(true);
            setQcConfirmationDetails({
                confirmedBy: confirmerName.trim(),
                confirmedAt: new Date().toISOString()
            });
            setConfirmationDialog(false);
            setConfirmerName('');
            showSnackbar?.('QC documentation confirmed successfully', 'success');
            
            // Notify parent component to update workflow progress
            onDataUpdate?.();
            
        } catch (error) {
            showSnackbar?.('Error confirming QC documentation: ' + error.message, 'error');
        }
    };

    const handleValidationChange = (stepId, isValid) => {
        setStepValidation(prev => {
            const newValidation = {
                ...prev,
                [stepId]: isValid
            };
            return newValidation;
        });
    };

    const handleSaveTest = async () => {
        try {
            
            // Prevent multiple saves
            if (formData._saving) {
                showSnackbar?.('Test is already being saved...', 'warning');
                return;
            }
            
            setFormData(prev => ({ ...prev, _saving: true }));

            // Helper function to convert empty strings to null for numeric fields
            const toNumericOrNull = (value) => {
                if (value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '')) {
                    return null;
                }
                return value;
            };

            // Include ALL form fields with proper null handling for numeric fields
            const testData = {
                batch_id: batchId,
                batch_log_id: batchLogId,
                company_id: companyId,
                product_id: productInfo?.id,
                product_name: productInfo?.name,
                product_sku: productInfo?.sku || productInfo?.product_code,
                batch_ingredient_id: formData.batch_ingredient_id || null,
                test_category: formData.test_category,
                test_stage: formData.test_stage,
                test_type: formData.test_type,
                test_method: formData.test_method,
                test_standard: formData.test_standard,
                target_value: toNumericOrNull(formData.target_value),
                target_unit: formData.target_unit,
                min_acceptable: toNumericOrNull(formData.min_acceptable),
                max_acceptable: toNumericOrNull(formData.max_acceptable),
                
                // Flexible specification fields
                specification_type: formData.specification_type || 'numeric',
                acceptance_criteria: formData.acceptance_criteria || null,
                scale_range_min: toNumericOrNull(formData.scale_range_min),
                scale_range_max: toNumericOrNull(formData.scale_range_max),
                acceptable_scale_values: formData.acceptable_scale_values || null,
                reference_standard: formData.reference_standard || null,
                max_delta_e: toNumericOrNull(formData.max_delta_e),
                min_time: toNumericOrNull(formData.min_time),
                max_time: toNumericOrNull(formData.max_time),
                actual_result: toNumericOrNull(formData.actual_result),
                result_text: formData.result_text,
                tested_by_name: formData.tested_by_name,
                tested_by_role: formData.tested_by_role,
                test_datetime: formData.test_datetime?.toISOString(),
                room_temperature_c: toNumericOrNull(formData.room_temperature_c),
                room_humidity_percent: toNumericOrNull(formData.room_humidity_percent),
                clean_room_classification: formData.clean_room_classification,
                temp_unit: formData.temp_unit,
                equipment_id: formData.equipment_id,
                equipment_name: formData.equipment_name,
                equipment_model: formData.equipment_model,
                equipment_serial: formData.equipment_serial,
                calibration_status: formData.calibration_status,
                calibration_date: formData.calibration_date?.toISOString() || null,
                calibration_due_date: formData.calibration_due_date?.toISOString() || null,
                sample_id: formData.sample_id,
                sample_location: formData.sample_location,
                sample_quantity: toNumericOrNull(formData.sample_quantity),
                sample_unit: formData.sample_unit,
                sample_retention_location: formData.sample_retention_location,
                qa_review_required: formData.qa_review_required,
                qa_review_status: 'PENDING',
                notes: formData.notes || '',
                photo_urls: formData.photo_urls || [],
                document_urls: formData.document_urls || [],
                is_critical_test: formData.is_critical_test,
                qa_signature: formData.qa_signature || null,
                qa_signature_name: formData.qa_signature_name || null,
                qa_signature_role: formData.qa_signature_role || null,
                qa_signature_date: formData.qa_signature_date?.toISOString() || null,
                qa_review_comments: formData.qa_review_comments || null,
                qa_reviewed_by_name: formData.qa_reviewed_by_name || null,
                qa_reviewed_by_role: formData.qa_reviewed_by_role || null,
                qa_review_datetime: formData.qa_review_datetime?.toISOString() || null,
                executed_by_signature: formData.executed_by_signature || null,
                executed_by_signature_name: formData.executed_by_signature_name || null,
                executed_by_signature_date: formData.executed_by_signature_date?.toISOString() || null,
                final_status: (() => {
                    const actualResult = toNumericOrNull(formData.actual_result);
                    const minAcceptable = toNumericOrNull(formData.min_acceptable);
                    const maxAcceptable = toNumericOrNull(formData.max_acceptable);
                    
                    if (actualResult !== null && minAcceptable !== null && maxAcceptable !== null) {
                        const actual = parseFloat(actualResult);
                        const min = parseFloat(minAcceptable);
                        const max = parseFloat(maxAcceptable);
                        return (actual >= min && actual <= max) ? 'PASS' : 'FAIL';
                    }
                    return 'PENDING';
                })()
            };

            let result;
            if (editingTest) {
                result = await supabase
                    .from('qc_tests')
                    .update(testData)
                    .eq('id', editingTest.id)
                    .select()
                    .single();
            } else {
                result = await supabase
                    .from('qc_tests')
                    .insert(testData)
                    .select()
                    .single();
            }

            if (result.error) {
                throw result.error;
            }
            showSnackbar?.('Production test saved successfully', 'success');
            setDialogOpen(false);
            resetForm();
            
            await fetchQCTests();
            
        } catch (error) {
            showSnackbar?.('Error saving test: ' + error.message, 'error');
        } finally {
            setFormData(prev => ({ ...prev, _saving: false }));
        }
    };

    const handleQAReview = async (approvalData) => {
        try {
            // Use testUnderReview instead of editingTest for the review process
            let testToApprove = testUnderReview;
            
            if (!testToApprove) {
                throw new Error('No test selected for review');
            }
            
            // Extract signature data from various possible sources in approvalData
            const signature = approvalData.signature || 
                            approvalData.qa_signature || 
                            approvalData.qa_signature_name || 
                            approvalData.reviewerName || 
                            approvalData.name || 
                            approvalData.test_record?.qa_signature_name;
                            
            const role = approvalData.reviewerRole || 
                        approvalData.qa_signature_role || 
                        approvalData.role || 
                        approvalData.test_record?.qa_signature_role;
                        
            const comments = approvalData.comments || 
                           approvalData.qa_review_comments || 
                           approvalData.test_record?.qa_review_comments;
            
            const updateData = {
                qa_review_status: 'APPROVED',
                qa_review_datetime: dayjs().toISOString(),
                qa_reviewed_by_name: signature,
                qa_reviewed_by_role: role,
                qa_signature: signature,
                qa_signature_name: signature,
                qa_signature_role: role,
                qa_signature_date: dayjs().toISOString(),
                qa_review_comments: comments || null,
                locked_at: dayjs().toISOString()
            };
            
            const { data, error } = await supabase
                .from('qc_tests')
                .update(updateData)
                .eq('id', testToApprove.id)
                .select()
                .single();

            if (error) {
                throw error;
            }

            // Refresh the QC tests list to show updated status
            await fetchQCTests();
            
            showSnackbar?.('QC test approved and locked', 'success');
            
        } catch (error) {
            showSnackbar?.('Error approving test: ' + error.message, 'error');
            throw error;
        }
    };

    const resetForm = () => {
        setFormData({
            batch_id: batchId,
            batch_log_id: batchLogId,
            product_id: productInfo?.id || '',
            product_name: productInfo?.name || '',
            product_sku: productInfo?.sku || productInfo?.product_code || '',
            batch_ingredient_id: '',
            test_category: 'in_process',
            test_stage: 'mixing',
            test_type: '',
            test_method: '',
            test_standard: '',
            target_value: '',
            target_unit: '',
            min_acceptable: '',
            max_acceptable: '',
            actual_result: '',
            result_text: '',
            tested_by_name: '',
            tested_by_role: 'qa_technician',
            test_datetime: dayjs(),
            room_temperature_c: '',
            room_humidity_percent: '',
            clean_room_classification: '',
            temp_unit: 'celsius',
            equipment_id: '',
            equipment_name: '',
            equipment_model: '',
            equipment_serial: '',
            calibration_status: 'not_required',
            calibration_date: null,
            calibration_due_date: null,
            sample_id: '',
            sample_location: '',
            sample_quantity: '',
            sample_unit: 'ml',
            sample_retention_location: '',
            qa_review_required: true,
            qa_review_status: 'PENDING',
            notes: '',
            photo_urls: [],
            document_urls: [],
            is_critical_test: false,
            
            // Flexible specification fields
            specification_type: 'numeric',
            acceptance_criteria: '',
            scale_range_min: '',
            scale_range_max: '',
            acceptable_scale_values: '',
            reference_standard: '',
            max_delta_e: '',
            min_time: '',
            max_time: '',
            qa_signature: null,
            qa_signature_name: null,
            qa_signature_role: null,
            qa_signature_date: null,
            qa_review_comments: null,
            qa_reviewed_by_name: null,
            qa_reviewed_by_role: null,
            qa_review_datetime: null,
            executed_by_signature: null,
            executed_by_signature_name: null,
            executed_by_signature_date: null
        });
        setStepValidation({
            configuration: false,
            specifications: false,
            environment: false,
            sampling: false,
            results: false,
            review: false
        });
        setEditingTest(null);
        setActiveStep(0);
    };

    const handleEditTest = (test) => {
        setEditingTest(test);
        setActiveStep(0);
        const editFormData = {
            ...test,
            test_datetime: dayjs(test.test_datetime),
            calibration_date: test.calibration_date ? dayjs(test.calibration_date) : null,
            calibration_due_date: test.calibration_due_date ? dayjs(test.calibration_due_date) : null,
            qa_signature_date: test.qa_signature_date ? dayjs(test.qa_signature_date) : null,
            qa_review_datetime: test.qa_review_datetime ? dayjs(test.qa_review_datetime) : null,
            executed_by_signature_date: test.executed_by_signature_date ? dayjs(test.executed_by_signature_date) : null
        };
        setFormData(editFormData);
        setDialogOpen(true);
    };

    const renderStepContent = (stepId) => {
        const commonProps = {
            formData,
            setFormData,
            onValidationChange: handleValidationChange,
            showSnackbar,
            batchId,
            batchNumber,
            productInfo,
            companyId,
            batchIngredients,
            canEdit,
            canDelete,
            canApprove,
            batchTimeFormat
        };

        switch (stepId) {
            case 'configuration':
                return <ConfigurationStep {...commonProps} />;
            case 'specifications':
                return <SpecificationsStep {...commonProps} />;
            case 'environment':
                return <EnvironmentStep {...commonProps} />;
            case 'sampling':
                return <SamplingStep {...commonProps} />;
            case 'results':
                return <ResultsStep {...commonProps} editingTest={editingTest} onSaveTest={handleSaveTest} />;
            default:
                return <Alert severity="error">Unknown step: {stepId}</Alert>;
        }
    };

    // Get the appropriate steps - always exclude review from main flow
    const getAvailableSteps = () => {
        return qcTimelineSteps.filter(step => step.id !== 'review');
    };

    const availableSteps = getAvailableSteps();

    const isFormComplete = () => {
        const requiredSteps = availableSteps.map(s => s.id);
        const isComplete = requiredSteps.every(stepId => stepValidation[stepId]);
        return isComplete;
    };

    const canProceedToNext = (stepIndex) => {
        const stepIds = availableSteps.map(s => s.id);
        const currentStepId = stepIds[stepIndex];
        return stepValidation[currentStepId];
    };

    // Calculate paginated tests
    const totalPages = Math.ceil(qcTests.length / testsPerPage);
    const startIndex = (currentPage - 1) * testsPerPage;
    const endIndex = startIndex + testsPerPage;
    const paginatedTests = qcTests.slice(startIndex, endIndex);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        setExpandedTests({}); // Collapse all tests when changing page
    };

    const handleDeleteTest = async () => {
        if (!testToDelete) return;
        
        try {
            const { error } = await supabase
                .from('qc_tests')
                .delete()
                .eq('id', testToDelete.id);

            if (error) throw error;

            showSnackbar?.('Production test deleted successfully', 'success');
            setDeleteDialogOpen(false);
            setTestToDelete(null);
            fetchQCTests();
        } catch (error) {
            showSnackbar?.('Error deleting QC test: ' + error.message, 'error');
        }
    };

    const handleDeleteClick = (test) => {
        setTestToDelete(test);
        setDeleteDialogOpen(true);
    };

    const handleReviewTest = (test) => {
        setTestUnderReview(test);
        setReviewDialogOpen(true);
    };

    const handleReviewComplete = async (approvalData) => {
        try {
            await handleQAReview(approvalData);
            
            setReviewDialogOpen(false);
            setTestUnderReview(null);
            
            // Force a refresh of the QC tests to show the updated signature
            await fetchQCTests();
            
        } catch (error) {
            showSnackbar?.('Error completing review: ' + error.message, 'error');
        }
    };

    // Function to update test under review data (for ReviewStep to update signature fields)
    const updateTestUnderReview = (updater) => {
        setTestUnderReview(prev => {
            if (typeof updater === 'function') {
                return updater(prev);
            }
            return updater;
        });
    };

    const canEdit = canEditPage('batch_logs_qc_checks');
    const canDelete = canDeletePage('batch_logs_qc_checks');
    const canApprove = canApproveQCTests();

    // Permission check for batch logs QC checks
    if (!canAccessPage('batch_logs_qc_checks')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                px: 3
            }}>
                <Card sx={{
                    p: 8,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 600,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 6,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2.5rem',
                        letterSpacing: '-0.8px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 4,
                        fontWeight: 500,
                        fontSize: '1.2rem'
                    }}>
                        QC Checks Log Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        maxWidth: 450,
                        mx: 'auto'
                    }}>
                        You don't currently have the necessary permissions to access the QC Checks Log. 
                        Contact your system administrator to request access or verify your role permissions.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                {/* Header Card */}
                <Card sx={{ 
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    mb: 3
                }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                        Production Quality Tests
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        {qcTests.length} Total Tests • Page {currentPage} of {totalPages}
                                        {qcTests.filter(t => t.final_status === 'PASS').length > 0 && (
                                            <> • {qcTests.filter(t => t.final_status === 'PASS').length} Passed</>
                                        )}
                                        {qcTests.filter(t => t.final_status === 'FAIL').length > 0 && (
                                            <> • {qcTests.filter(t => t.final_status === 'FAIL').length} Failed</>
                                        )}
                                        {qcTests.filter(t => t.qa_review_status === 'REVIEWED').length > 0 && (
                                            <> • {qcTests.filter(t => t.qa_review_status === 'REVIEWED').length} QA Reviewed</>
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                {qcDocumentationConfirmed ? (
                                    <Chip
                                        icon={<CheckCircleIcon sx={{ color: 'white !important' }}/>}
                                        label="QC Documentation Confirmed"
                                        sx={{
                                            backgroundColor: theme.palette.success.main,
                                            color: 'white',
                                            fontWeight: 500,
                                            px: 4,
                                            py: 1.5,
                                            height: 'auto',
                                            fontSize: '1rem',
                                            '& .MuiChip-icon': {
                                                color: 'white !important'
                                            },
                                            '& .MuiChip-label': {
                                                fontSize: '1rem',
                                                fontWeight: 500
                                            }
                                        }}
                                    />
                                ) : qcTests.length > 0 ? (
                                    <Button
                                        variant="contained"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => setConfirmationDialog(true)}
                                        disabled={!canApprove || qcTests.filter(test => test.qa_review_status !== 'APPROVED' && test.qa_review_status !== 'REVIEWED').length > 0}
                                        sx={{
                                            borderRadius: 3,
                                            px: 3,
                                            py: 1.5,
                                            background: canApprove ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})` : theme.palette.grey[400],
                                            color: canApprove ? 'white' : theme.palette.grey[100],
                                            fontWeight: 500,
                                            boxShadow: canApprove ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}` : 'none',
                                            '&:hover': {
                                                background: canApprove ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})` : theme.palette.grey[400],
                                                transform: canApprove ? 'translateY(-1px)' : 'none',
                                                boxShadow: canApprove ? `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}` : 'none'
                                            },
                                            '&:disabled': {
                                                background: theme.palette.grey[400],
                                                color: theme.palette.grey[100]
                                            },
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}
                                    >
                                        {canApprove ? 'Confirm QC Documentation' : 'No Approval Permissions'}
                                    </Button>
                                ) : null}
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        resetForm();
                                        setEditingTest(null);
                                        setActiveStep(0);
                                        setDialogOpen(true);
                                    }}
                                    size="large"
                                    disabled={!canEdit}
                                    sx={{
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.5,
                                        background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` : theme.palette.grey[400],
                                        color: canEdit ? 'white' : theme.palette.grey[100],
                                        fontWeight: 500,
                                        boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                                        '&:hover': {
                                            background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` : theme.palette.grey[400],
                                            transform: canEdit ? 'translateY(-1px)' : 'none',
                                            boxShadow: canEdit ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}` : 'none'
                                        },
                                        '&:disabled': {
                                            background: theme.palette.grey[400],
                                            color: theme.palette.grey[100]
                                        },
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    {canEdit ? 'Add Quality Test' : 'Read Only - No Edit Permissions'}
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* QC Documentation Confirmation Signature */}
                {qcDocumentationConfirmed && qcConfirmationDetails && (
                    <Card sx={{ 
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.success.main}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.04)})`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.success.main, 0.12)}`,
                        mb: 3,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.success.main, 0.18)}`,
                        }
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.success.main, 0.15)}, ${alpha(theme.palette.success.main, 0.08)})`,
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <CheckCircleIcon sx={{ fontSize: 24, color: theme.palette.success.main }} />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.success.main, mb: 0.5 }}>
                                        QC Documentation Confirmed
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#000000', fontWeight: 500 }}>
                                        Confirmed by: {qcConfirmationDetails.confirmedBy}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#000000', fontWeight: 500 }}>
                                        Date & Time: {dayjs(qcConfirmationDetails.confirmedAt).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                    </Typography>
                                </Box>
                                <Chip
                                    icon={<CheckCircleIcon sx={{ color: 'white !important' }}/>}
                                    label="APPROVED"
                                    sx={{
                                        backgroundColor: theme.palette.success.main,
                                        color: 'white',
                                        fontWeight: 500,
                                        px: 2,
                                        py: 1,
                                        fontSize: '0.9rem'
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Test Cards */}
                <Stack spacing={2}>
                {loading ? (
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ textAlign: 'center', py: 8 }}>
                                <ScienceIcon sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 3 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                                    Loading QC tests...
                                </Typography>
                            </CardContent>
                        </Card>
                ) : qcTests.length === 0 ? (
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            background: 'white',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                            <CardContent sx={{ textAlign: 'center', py: 8 }}>
                                <ScienceIcon sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 3 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                                    No quality tests recorded yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Start by adding the first quality test to begin the testing log
                                </Typography>
                            </CardContent>
                        </Card>
                ) : (
                        <>
                            {paginatedTests.map((test) => (
                                <TestCard
                                    key={test.id}
                                    test={test}
                                    isExpanded={expandedTests[test.id]}
                                    onToggleExpanded={() => setExpandedTests(prev => ({
                                        ...prev,
                                        [test.id]: !prev[test.id]
                                    }))}
                                    onEdit={canEdit ? handleEditTest : null}
                                    onTrendAnalysis={(productId, testType) => {
                                        showSnackbar?.('Trend analysis feature coming soon', 'info');
                                    }}
                                    onQAReview={canApprove ? handleQAReview : null}
                                    onReview={canApprove ? handleReviewTest : null}
                                    QAReviewForm={() => null}
                                    batchNumber={batchNumber}
                                    productInfo={productInfo}
                                    onDeleteClick={canDelete ? handleDeleteClick : null}
                                    canEdit={canEdit}
                                    canDelete={canDelete}
                                    canApprove={canApprove}
                                    batchTimeFormat={batchTimeFormat}
                                />
                            ))}
                            
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                borderRadius: 3,
                                                fontWeight: 500,
                                                mx: 0.5,
                                                backgroundColor: 'white',
                                                backdropFilter: 'blur(10px)',
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                                    border: `1px solid ${theme.palette.primary.main}`
                                                }
                                            },
                                            '& .Mui-selected': {
                                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                                color: 'white',
                                                border: `1px solid ${theme.palette.primary.main}`,
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                '&:hover': {
                                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </>
                )}
                </Stack>

                {/* QC Test Dialog */}
                <Dialog 
                    open={dialogOpen} 
                    onClose={() => setDialogOpen(false)}
                    maxWidth={false}
                    fullWidth
                    PaperProps={{
                        sx: {
                            width: '70vw',
                            height: '95vh',
                            maxWidth: 'none',
                            maxHeight: 'none',
                            m: 1,
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        fontWeight: 500,
                        fontSize: '1.2rem',
                        color: 'primary.main',
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: alpha(theme.palette.primary.main, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 3
                    }}>
                        <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                            {editingTest ? 'Edit Production Test' : 'New Production Test'}: {productInfo?.name} • Batch {batchNumber || batchId}
                        </Typography>
                    </DialogTitle>
                    
                    <DialogContent sx={{ 
                        backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                        p: 3,
                        maxHeight: '80vh', 
                        overflowY: 'auto',
                        backdropFilter: 'blur(10px)',
                        mt: 3
                    }}>
                        <Box sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column'
                        }}>
                            {/* Main Content Area */}
                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
                                    {availableSteps.map((step, index) => {
                                        const isCompleted = stepValidation[step.id];
                                        const stepIcon = {
                                            configuration: <ScienceIcon />,
                                            specifications: <AssignmentIcon />,
                                            environment: <EngineeringIcon />,
                                            sampling: <DescriptionIcon />,
                                            results: <AnalyticsIcon />,
                                            review: <VerifiedUserIcon />
                                        };
                                        
                                        return (
                                            <Step key={step.id} completed={isCompleted}>
                                                <StepLabel 
                                                    StepIconComponent={() => (
                                                        <Avatar sx={{ 
                                                            backgroundColor: isCompleted ? theme.palette.success.main : 
                                                                            activeStep === index ? step.color : theme.palette.grey[400],
                                                            color: 'white',
                                                            width: 40,
                                                            height: 40,
                                                            cursor: isCompleted || index <= activeStep ? 'pointer' : 'default'
                                                        }}>
                                                            {isCompleted ? <CheckCircleIcon /> : stepIcon[step.id]}
                                                        </Avatar>
                                                    )}
                                                    onClick={() => {
                                                        if (isCompleted || index <= activeStep) {
                                                            setActiveStep(index);
                                                        }
                                                    }}
                                                    sx={{
                                                        cursor: isCompleted || index <= activeStep ? 'pointer' : 'default',
                                                        '&:hover': {
                                                            backgroundColor: isCompleted || index <= activeStep ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                                        }
                                                    }}
                                                >
                                                    <Typography 
                                                        variant="h6" 
                                                        sx={{ 
                                                            fontWeight: 500, 
                                                            color: isCompleted ? theme.palette.success.main : 'inherit',
                                                            cursor: isCompleted || index <= activeStep ? 'pointer' : 'default'
                                                        }}
                                                        onClick={() => {
                                                            if (isCompleted || index <= activeStep) {
                                                                setActiveStep(index);
                                                            }
                                                        }}
                                                    >
                                                        {step.title}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        color="text.secondary"
                                                        sx={{
                                                            cursor: isCompleted || index <= activeStep ? 'pointer' : 'default'
                                                        }}
                                                        onClick={() => {
                                                            if (isCompleted || index <= activeStep) {
                                                                setActiveStep(index);
                                                            }
                                                        }}
                                                    >
                                                        {step.description}
                                                    </Typography>
                                                </StepLabel>
                                                <StepContent>
                                                    <Box sx={{ mt: 3 }}>
                                                        {renderStepContent(step.id)}
                                                    </Box>
                                                    <Box sx={{ mt: 4, mb: 2 }}>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => setActiveStep(activeStep + 1)}
                                                            sx={{ 
                                                                mr: 2,
                                                                borderRadius: 2,
                                                                px: 3,
                                                                py: 1.5,
                                                                color: theme.palette.text.secondary,
                                                                fontWeight: 500,
                                                                background: 'rgba(255, 255, 255, 0.8)',
                                                                backdropFilter: 'blur(10px)',
                                                                '&:hover': {
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                                                }
                                                            }}
                                                            disabled={index === availableSteps.length - 1}
                                                        >
                                                            Next Step
                                                        </Button>
                                                        <Button
                                                            disabled={index === 0}
                                                            onClick={() => setActiveStep(activeStep - 1)}
                                                            sx={{ 
                                                                borderRadius: 2,
                                                                px: 3,
                                                                py: 1.5,
                                                                color: theme.palette.text.secondary,
                                                                fontWeight: 500,
                                                                background: 'rgba(255, 255, 255, 0.8)',
                                                                backdropFilter: 'blur(10px)',
                                                                '&:hover': {
                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                                                }
                                                            }}
                                                            variant="outlined"
                                                        >
                                                            Previous Step
                                                        </Button>
                                                        
                                                        {/* Save/Update Button at the last step */}
                                                        {index === availableSteps.length - 1 && (
                                                            <Button
                                                                variant="contained"
                                                                onClick={handleSaveTest}
                                                                disabled={!canEdit || !isFormComplete() || formData._saving}
                                                                startIcon={<SaveIcon />}
                                                                sx={{
                                                                    ml: 2,
                                                                    borderRadius: 2,
                                                                    px: 4,
                                                                    py: 1.5,
                                                                    background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` : theme.palette.grey[400],
                                                                    color: canEdit ? 'white' : theme.palette.grey[100],
                                                                    fontWeight: 500,
                                                                    boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                                                                    '&:hover': {
                                                                        background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` : theme.palette.grey[400],
                                                                        boxShadow: canEdit ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}` : 'none'
                                                                    },
                                                                    '&:disabled': {
                                                                        background: theme.palette.grey[400],
                                                                        color: theme.palette.grey[100]
                                                                    }
                                                                }}
                                                            >
                                                                {!canEdit ? 'Read Only - No Edit Permissions' : (formData._saving ? 'Saving...' : (editingTest ? 'Update Test' : 'Save Test'))}
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </StepContent>
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                            </Box>
                        </Box>
                    </DialogContent>
                    
                    <DialogActions sx={{ 
                        p: 3, 
                        gap: 2, 
                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                    }}>
                        <Button onClick={() => {
                            setDialogOpen(false);
                        }} 
                        startIcon={<CancelIcon />}
                        sx={{ 
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            background: 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.04)
                            }
                        }}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSaveTest} 
                            variant="contained"
                            startIcon={<SaveIcon />}
                            disabled={!canEdit || !isFormComplete() || formData._saving}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` : theme.palette.grey[400],
                                color: canEdit ? 'white' : theme.palette.grey[100],
                                fontWeight: 500,
                                boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                                '&:hover': {
                                    background: canEdit ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` : theme.palette.grey[400],
                                    boxShadow: canEdit ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}` : 'none'
                                },
                                '&:disabled': {
                                    background: theme.palette.grey[400],
                                    color: theme.palette.grey[100]
                                }
                            }}
                        >
                            {!canEdit ? 'Read Only - No Edit Permissions' : (formData._saving ? 'Saving...' : (editingTest ? 'Update Test' : 'Save Test'))}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                            maxHeight: '90vh',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        fontWeight: 500,
                        fontSize: '1.2rem',
                        color: theme.palette.error.main,
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: alpha(theme.palette.error.main, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 3
                    }}>
                        Confirm Delete Test
                    </DialogTitle>
                    <DialogContent sx={{ 
                        backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                        p: 3,
                        backdropFilter: 'blur(10px)',
                        mt: 3
                    }}>
                        <Typography variant="body1" sx={{ mb: 2, mt: 2, fontWeight: 500 }}>
                            Are you sure you want to delete this quality test?
                        </Typography>
                        {testToDelete && (
                            <Box sx={{ 
                                p: 3, 
                                backgroundColor: alpha(theme.palette.grey[100], 0.8), 
                                borderRadius: 3, 
                                border: `2px solid ${theme.palette.grey[400]}`, 
                                mb: 3
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                    {testTypes.find(t => t.value === testToDelete.test_type)?.label || testToDelete.test_type}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Result: {testToDelete.actual_result || testToDelete.result_text || '—'} {testToDelete.target_unit}
                                    {testToDelete.final_status && ` • Status: ${testToDelete.final_status}`}
                                    {testToDelete.test_datetime && ` • ${dayjs(testToDelete.test_datetime).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}`}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ 
                        p: 3, 
                        gap: 2, 
                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                    }}>
                        <Button 
                            onClick={() => setDeleteDialogOpen(false)}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                background: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleDeleteTest}
                            variant="contained"
                            startIcon={<DeleteIcon />}
                            disabled={!canDelete}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                background: canDelete ? `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})` : theme.palette.grey[400],
                                color: canDelete ? 'white' : theme.palette.grey[100],
                                fontWeight: 500,
                                boxShadow: canDelete ? `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` : 'none',
                                '&:hover': {
                                    background: canDelete ? `linear-gradient(135deg, ${theme.palette.error.dark}, ${theme.palette.error.main})` : theme.palette.grey[400],
                                    boxShadow: canDelete ? `0 6px 20px ${alpha(theme.palette.error.main, 0.4)}` : 'none'
                                },
                                '&:disabled': {
                                    background: theme.palette.grey[400],
                                    color: theme.palette.grey[100]
                                }
                            }}
                        >
                            {canDelete ? 'Delete Test' : 'Read Only - No Delete Permissions'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Review Dialog */}
                <Dialog
                    open={reviewDialogOpen}
                    onClose={() => setReviewDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                            maxHeight: '90vh',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        fontWeight: 500,
                        fontSize: '1.2rem',
                        color: 'primary.main',
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: alpha(theme.palette.primary.main, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 3
                    }}>
                        <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                            QA Review & Approval
                        </Typography>
                    </DialogTitle>
                    
                    <DialogContent sx={{ p: 3 }}>
                        {testUnderReview && (
                            <ReviewStep
                                formData={testUnderReview}
                                setFormData={updateTestUnderReview}
                                onValidationChange={() => {}}
                                showSnackbar={showSnackbar}
                                batchId={batchId}
                                batchNumber={batchNumber}
                                productInfo={productInfo}
                                companyId={companyId}
                                editingTest={testUnderReview}
                                onApproval={handleReviewComplete}
                                isReviewMode={true}
                                batchTimeFormat={batchTimeFormat}
                            />
                        )}
                    </DialogContent>
                </Dialog>

                {/* QC Documentation Confirmation Dialog */}
                <Dialog
                    open={confirmationDialog}
                    onClose={() => setConfirmationDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                            maxHeight: '90vh',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <DialogTitle sx={{ 
                        fontWeight: 500,
                        fontSize: '1.2rem',
                        color: theme.palette.success.main,
                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: alpha(theme.palette.success.main, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 3
                    }}>
                        Confirm QC Documentation
                    </DialogTitle>
                    <DialogContent sx={{ 
                        backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                        p: 3,
                        backdropFilter: 'blur(10px)',
                        mt: 3
                    }}>
                        {(() => {
                            const pendingTests = qcTests.filter(test => 
                                test.qa_review_status !== 'APPROVED' && test.qa_review_status !== 'REVIEWED'
                            );
                            
                            if (pendingTests.length > 0) {
                                return (
                                    <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                                        <Typography sx={{ color: '#000000', fontWeight: 500 }}>
                                            Cannot confirm QC documentation: {pendingTests.length} test(s) pending review. Please review all tests first.
                                        </Typography>
                                    </Alert>
                                );
                            }
                            
                            return (
                                <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                                    You are confirming that all required QC tests have been documented for this batch.
                                </Alert>
                            );
                        })()}
                        
                        <TextField
                            fullWidth
                            label="Your Name (Required)"
                            value={confirmerName}
                            onChange={(e) => setConfirmerName(e.target.value)}
                            placeholder={canApprove ? "Enter your full name" : "No approval permissions"}
                            required
                            disabled={!canApprove}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: theme.palette.primary.main, borderWidth: '1px' },
                                    '&:hover fieldset': { borderColor: theme.palette.primary.main, borderWidth: '1px' },
                                    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: '1px' }
                                }
                            }}
                        />
                        
                        <Box sx={{ 
                            p: 2, 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 2, 
                            backgroundColor: '#f9f9f9',
                            mb: 2
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                                QC Testing Summary:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Total Tests:</strong> {qcTests.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Passed:</strong> {qcTests.filter(t => t.final_status === 'PASS').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Failed:</strong> {qcTests.filter(t => t.final_status === 'FAIL').length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <strong>QA Reviewed:</strong> {qcTests.filter(t => t.qa_review_status === 'APPROVED').length}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 500 }}>
                            Are you sure you want to confirm that all QC documentation is complete for this batch?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ 
                        p: 3, 
                        gap: 2, 
                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                    }}>
                        <Button 
                            onClick={() => {
                                setConfirmationDialog(false);
                                setConfirmerName('');
                            }}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                                background: 'rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleConfirmQCDocumentation}
                            variant="contained"
                            startIcon={<CheckCircleIcon />}
                            disabled={!canApprove || !confirmerName.trim() || qcTests.filter(test => test.qa_review_status !== 'APPROVED' && test.qa_review_status !== 'REVIEWED').length > 0}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                background: canApprove ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})` : theme.palette.grey[400],
                                color: canApprove ? 'white' : theme.palette.grey[100],
                                fontWeight: 500,
                                boxShadow: canApprove ? `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}` : 'none',
                                '&:hover': {
                                    background: canApprove ? `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})` : theme.palette.grey[400],
                                    boxShadow: canApprove ? `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}` : 'none'
                                },
                                '&:disabled': {
                                    background: theme.palette.grey[400],
                                    color: theme.palette.grey[100]
                                }
                            }}
                        >
                            {canApprove ? 'Confirm Documentation' : 'No Approval Permissions'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
} 